import React, { useMemo, useState } from "react";
import { useTheme } from "../../hooks/useTheme";

type DataPoint = {
  name: string;
  value: number;
};

type BarGraphProps = {
  data: DataPoint[];
  title: string
  maxHeight?: number;
};

export const CustomBarGraph: React.FC<BarGraphProps> = ({ data, title, maxHeight = 200 }) => {
  const { themeStyles } = useTheme();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const dataMaxValue = Math.max(...data.map((d) => d.value), 1);
  const maxValue = Math.ceil(dataMaxValue / 10) * 10;
  const tickCount = 7;

  const yTicks = Array.from({ length: tickCount + 1 }, (_, i) => {
    return Math.round((i * maxValue) / tickCount);
  });

  const randomColors = useMemo(
    () => data.map(() => `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, "0")}`),
    [data]
  );

  return (
    <div
      className={`w-full py-2 ml-2 mt-2 flex flex-col gap-1.5 relative ${themeStyles.main.tailwindMainBg} 
      border ${themeStyles.main.borderColor} rounded-[16px] relative`}
    >
      <h2 className={`text-lg font-semibold px-5 ${themeStyles.main.textHeadingColor}`}>
        {title}
      </h2>
      <div className="flex px-2">
        {/* Y-Axis (Scale) */}
        <div className="flex flex-col pr-4 text-xs text-gray-500 mt-[40px] gap-[8px]">
          {yTicks.reverse().map((tick) => (
            <div key={tick} className="h-[20px] flex items-center justify-end pr-2">
              {tick}
            </div>
          ))}
        </div>

        {/* Graph Container */}
        <div className="overflow-x-auto w-full" style={{
          height: `${maxHeight + 100}px`
        }}>
          <div className="flex space-x-4 pb-2 w-max relative items-end h-full">
            {data.map((point, index) => {
              const barHeight = point.value > 0 ? (point.value / maxValue) * maxHeight : 5;

              return (
                <div key={index} className="flex flex-col items-center w-16 relative">
                  {/* Bar Container */}
                  <div
                    className="relative w-12 flex flex-col items-center"
                    onMouseEnter={() => setHoverIndex(index)}
                    onMouseLeave={() => setHoverIndex(null)}
                  >
                    {/* Tooltip */}
                    {hoverIndex === index && (
                      <div
                        className={`absolute px-2 py-1 bg-gray-800 text-white text-xs rounded-md z-10`}
                        style={{
                          top: `-${barHeight + 40}px`, // Adjust dynamically based on bar height
                        }}
                      >
                        {point.value}
                      </div>
                    )}

                    {/* Bar */}
                    <div
                      className={`absolute bottom-0 w-10 rounded-t-md transition-all duration-300 cursor-pointer
                      ${hoverIndex !== null && hoverIndex !== index ? "opacity-40" : "opacity-100"}`}
                      style={{
                        height: `${barHeight}px`,
                        backgroundColor: randomColors[index],
                      }}
                    />
                  </div>

                  {/* Label (Ensuring Fixed Bottom Alignment) */}
                  <div className="h-[30px] flex items-center text-center">
                    <span className="mt-2 text-xs text-gray-700">{point.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};


